import { Injectable } from '@angular/core';
import { BudgetComponentType } from '@coin/shared/core/util-enums';
import { RoundOperations, TinyHelpers } from '@coin/shared/core/util-helpers';
import { MeritBudgetComponent } from '../models/merit-budget-component.model';
import { EmployeeWithCompensationInfo } from '../models/merit-budget-direct-with-compensation.model';
import { UpdateAllocationValuesDto } from '../models/update-allocation-values-dto.model';

const isPresent = (value: number | null | undefined): boolean => value !== null && value !== undefined;

@Injectable()
export class UpdateAllocationValuesService {
  public getChangedComponentUpdateBody(
    direct: EmployeeWithCompensationInfo,
    changedComponent: MeritBudgetComponent,
    isLocalCurrency = true
  ): UpdateAllocationValuesDto {
    const body: UpdateAllocationValuesDto = {
      isDone: direct.isDone,
      simulationMemberId: direct.simulationMemberId
    };

    const round = (value: number): number => RoundOperations.round(value, 2);

    switch (changedComponent.type) {
      case BudgetComponentType.TotalTargetCash:
        body.totalTargetCash = round(changedComponent.totalAmount);
        break;
      case BudgetComponentType.BaseSalary:
        body.baseSalary = round(changedComponent.totalAmount);
        break;
      case BudgetComponentType.BasePay:
        body.basePay = round(changedComponent.totalAmount);
        break;
      case BudgetComponentType.TargetIncentiveAmount:
        if (isPresent(changedComponent.totalAmount)) {
          body.targetIncentiveAmount = round(changedComponent.totalAmount);
        } else if (isPresent(changedComponent.percentageOfBase)) {
          body.targetIncentiveAmountPoB = changedComponent.percentageOfBase;
        }
        break;
      case BudgetComponentType.FixedAllowances:
        body.fixedAllowances = round(changedComponent.totalAmount);
        break;
      case BudgetComponentType.FlatAllowances:
        body.flatAllowances = round(changedComponent.totalAmount);
        break;
      case BudgetComponentType.AllowancesPercentageInTotal:
        if (isPresent(changedComponent.totalAmount)) {
          body.allowancesPercentageTotal = round(changedComponent.totalAmount);
        } else if (isPresent(changedComponent.percentageOfBase)) {
          body.allowancesPoB = changedComponent.percentageOfBase;
        }
        break;
      case BudgetComponentType.AnnualPayment:
        body.annualPayment = round(changedComponent.totalAmount);
        break;
      case BudgetComponentType.OneTimePayment:
        body.oneTimePayment = round(changedComponent.totalAmount);
        break;
      case BudgetComponentType.Equity:
        body.equityType = changedComponent.equityType;
        if (isLocalCurrency) {
          body.equityAmount = round(changedComponent.localTotalAmount);
        } else {
          body.equityAmountInEuro = round(changedComponent.totalAmount);
        }
        break;
      case BudgetComponentType.TotalDirectCompensation:
        break;
      default:
        TinyHelpers.assertNever(changedComponent.type);
    }

    return body;
  }

  public getIsDoneOnlyUpdateBody(direct: EmployeeWithCompensationInfo): UpdateAllocationValuesDto {
    return {
      isDone: direct.isDone,
      simulationMemberId: direct.simulationMemberId
    };
  }

  // DEBUG: temporary defense-in-depth check for silent BE rejections (BE-PUNCH-LIST.md #2 / #6).
  // The BE responds 200 OK but with the OLD value instead of what we sent. Until the BE returns
  // 4xx instead, surface a debug warning so users see what happened. Remove once BE is fixed.
  public detectSilentRejections(
    sentBody: UpdateAllocationValuesDto,
    response: UpdateAllocationValuesDto
  ): Array<{ field: string; sent: number; received: number }> {
    const rejected: Array<{ field: string; sent: number; received: number }> = [];
    for (const key of Object.keys(sentBody) as Array<keyof UpdateAllocationValuesDto>) {
      const sent = sentBody[key];
      if (typeof sent !== 'number') continue;
      const received = response[key];
      if (typeof received !== 'number') continue;
      if (Math.abs(sent - received) > 0.01) {
        rejected.push({ field: String(key), sent, received });
      }
    }
    return rejected;
  }

  public applyResponseToComponents(components: MeritBudgetComponent[], response: UpdateAllocationValuesDto): void {
    if (!components || !response) {
      return;
    }

    for (const component of components) {
      switch (component.type) {
        case BudgetComponentType.TotalTargetCash:
          component.totalAmount = response.totalTargetCash;
          break;
        case BudgetComponentType.BaseSalary:
          component.totalAmount = response.baseSalary;
          break;
        case BudgetComponentType.BasePay:
          component.totalAmount = response.basePay;
          break;
        case BudgetComponentType.TargetIncentiveAmount:
          component.totalAmount = response.targetIncentiveAmount;
          if (isPresent(response.targetIncentiveAmountPoB)) {
            component.percentageOfBase = response.targetIncentiveAmountPoB;
          }
          break;
        case BudgetComponentType.FixedAllowances:
          component.totalAmount = response.fixedAllowances;
          break;
        case BudgetComponentType.FlatAllowances:
          component.totalAmount = response.flatAllowances;
          break;
        case BudgetComponentType.AllowancesPercentageInTotal:
          component.totalAmount = response.allowancesPercentageTotal;
          if (isPresent(response.allowancesPoB)) {
            component.percentageOfBase = response.allowancesPoB;
          }
          break;
        case BudgetComponentType.AnnualPayment:
          component.totalAmount = response.annualPayment;
          break;
        case BudgetComponentType.OneTimePayment:
          component.totalAmount = response.oneTimePayment;
          break;
        case BudgetComponentType.Equity:
          if (isPresent(response.equityAmountInEuro)) {
            component.totalAmount = response.equityAmountInEuro;
          }
          if (isPresent(response.equityAmount)) {
            component.localTotalAmount = response.equityAmount;
          }
          if (response.equityType) {
            component.equityType = response.equityType;
          }
          break;
        case BudgetComponentType.TotalDirectCompensation:
          break;
        default:
          TinyHelpers.assertNever(component.type);
      }
    }
  }
}
